'use client';

// Required: the `@aurorasolar/ds` barrel calls createContext at module scope, so this can't be an RSC.
// Copy source: "About Aurora Marketplace" doc — doc copy is authoritative.
// Layout adapts the Figma "Consumer Site Improvements" design (node 2117:11956). Where the Figma
// reworded a doc line, the doc wins.
// Cleared to launch 2026-08-03 (Nick + Karen): the doc's marketing-claim open items are accepted as
// accurate as of today. Legal is still discussing the lead-selling wording — if that lands otherwise,
// the S2 "Not a lead seller" card and the S4 col-1 closing sentence are what change.

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import Image from 'next/image';
import { ReactNode } from 'react';

import { LocationSubmit } from '@/app/components';
import { AsSeenIn, Eyebrow } from '@/app/home-solar/components';
import { familyEvHomeSolar, seniorCoupleReviewingBills } from '@/app/lp/assets';
import { ButtonLink, ColumnSection, H2, H3, Section, TrustIndicators } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';

const QUOTE_PATH = '/quote-builder';

const GetEstimatesButton = () => (
  <ButtonLink
    event={{ name: TRACKING.LANDING_CLICK_GET_QUOTE }}
    icon={DS.IconArrowRight}
    iconPosition="right"
    path={QUOTE_PATH}
    size={{ _: 'sm', md: 'md' }}
    variant="primary"
  >
    Get your estimates
  </ButtonLink>
);

// Stacked rather than side-by-side so each metric scales down cleanly on mobile (Grace, 7/29)
const Stat = ({ label, value }: { label: string; value: string }) => (
  <DS.Flex alignItems="center" gap={1} textAlign="center" w="auto">
    <DS.Text text={{ _: 'h1', md: 'd2' }}>{value}</DS.Text>
    <DS.Text color="uiHelperCopy" maxW="220px" text="body14">
      {label}
    </DS.Text>
  </DS.Flex>
);

const NotCard = ({ children, label }: { children: ReactNode; label: string }) => (
  <DS.Flex bg="uiBgAlternate" borderRadius={12} flexDirection="row" gap={4} h="100%" p={6}>
    {/* mt nudges the icon's optical centre onto the heading's first line (Grace, 7/29) */}
    <DS.IconXCircle color="purple-400" mt={1} size={5} />
    <DS.Flex flex={1} gap={2}>
      <H3>{label}</H3>
      <p>{children}</p>
    </DS.Flex>
  </DS.Flex>
);

const ComparisonCard = ({ children, title }: { children: ReactNode; title: string }) => (
  <DS.Flex bg="white" border="1px solid" borderColor="uiDivider" borderRadius={12} gap={3} h="100%" p={6}>
    <H3>{title}</H3>
    <p>{children}</p>
  </DS.Flex>
);

const CheckItem = ({ children }: { children: ReactNode }) => (
  <DS.Flex alignItems="flex-start" flexDirection="row" gap={2}>
    <DS.IconCheck color="uiCard.successText" size={4} />
    <DS.Text flex={1} text="body14">
      {children}
    </DS.Text>
  </DS.Flex>
);

export const AboutTheMarketplacePage = () => {
  return (
    <article>
      {/* S1 — Hero (Figma: dark #010916 split band, copy left + image right; content-height, not full-viewport) */}
      {/* Hero visual is a cleared consumer asset (also live on the electrification calculator and the
          homepage variations); design may still swap it for a final choice. */}
      <DS.DarkModeProvider>
        <x.section bg="black" mt={-60} px={{ _: 6, lg: 10 }}>
          <DS.Flex
            alignItems="center"
            flexDirection={{ _: 'column', lg: 'row' }}
            gap={{ _: 8, lg: 10 }}
            maxW={DS.BorealisTheme.screens.xl}
            mx="auto"
            pb={{ _: 8, lg: 10 }}
            pt={{ _: 110, lg: 130 }}
          >
            <DS.Flex flex={{ lg: '3 1 0' }} gap={5}>
              <Eyebrow color="white">We&rsquo;re not an installer</Eyebrow>
              <DS.Text as="h1" text={{ _: 'd1', lg: 'd3', md: 'd2' }}>
                Aurora is an independent solar marketplace.
              </DS.Text>
              <DS.Text text={{ _: 'body16', md: 'body20' }}>
                You already compare listings before you buy a home and read reviews before you book a trip. Aurora
                Marketplace works the same way for solar. Local companies compete for your project, and you see full
                estimates &mdash; system sizes, costs, payment options &mdash; before you share your name, email, or
                phone number with anyone. You decide who contacts you, and when.
              </DS.Text>
              <DS.Flex gap={2} maxW="526px" mt={2}>
                <LocationSubmit variant="cta">Get your estimates</LocationSubmit>
                <TrustIndicators />
              </DS.Flex>
            </DS.Flex>
            <x.div
              borderRadius={16}
              flex={{ lg: '2 1 0' }}
              h={{ _: 240, lg: 333, md: 320 }}
              maxW={{ lg: '484px' }}
              overflow="hidden"
              position="relative"
              w="100%"
            >
              {/* 1080px = one below DS `lg` (1081), where the band switches to two columns */}
              <Image
                alt="A family with an electric vehicle outside their solar-powered home"
                fill
                priority
                sizes="(max-width: 1080px) 100vw, 484px"
                src={familyEvHomeSolar}
                style={{ objectFit: 'cover' }}
              />
            </x.div>
          </DS.Flex>
        </x.section>
      </DS.DarkModeProvider>

      {/* Stats strip (Figma: moved up to directly under the hero) */}
      <Section align="center">
        <DS.Flex flexDirection={{ _: 'column', md: 'row' }} gap={{ _: 6, md: 9 }} justifyContent="center">
          <Stat label="solar projects designed with Aurora" value="20M+" />
          <Stat label="solar companies build on Aurora" value="7,000+" />
          <Stat label="years in solar technology" value="13" />
        </DS.Flex>
      </Section>

      {/* S3 — Why solar shopping needed fixing (Figma: 3-column comparison; Aurora column highlighted) */}
      <Section align="center" bg="uiBgAlternate" pb={{ _: 7, lg: 7 }}>
        <H2>There&rsquo;s no easy, low-pressure way to shop for solar.</H2>
        <DS.Text maxW="800px" mx="auto" text="body16">
          Today you have two real options. Going direct to an installer means working with one company at a time, on
          their terms. Using a comparison site means handing over your contact details before you see a single number.
          Neither path was built around you deciding on your own timeline.
        </DS.Text>
        <DS.Text as="p" mt={2} text={{ _: 'h3', md: 'h2' }}>
          No one built a third option. So we did.
        </DS.Text>
      </Section>
      {/* S3 competitor claims verified vs the June 5, 2026 teardown and cleared to launch (2026-08-03).
          Competitors A/B-test their funnels, so re-verify if these lines are ever challenged. */}
      <ColumnSection bg="uiBgAlternate" columnGap={7} columns={3} pt={{ _: 7, lg: 5 }} rowGap={7}>
        <ComparisonCard title="Going direct">
          When you contact an installer directly, you&rsquo;re talking to one company with one perspective, and their
          process is built to close. If you want to compare, you&rsquo;re managing multiple separate conversations, each
          proposal built on different equipment and assumptions, arriving at different times. Even when you have them
          all in front of you, the trade-offs aren&rsquo;t apparent. Which is actually the better value? That work falls
          entirely on you.
        </ComparisonCard>
        <ComparisonCard title="Using a comparison marketplace">
          Comparison sites bring multiple installers into one place, which solves part of the problem. But the estimates
          sit behind a contact gate: you provide your name, email, and phone number before you see a single number. Once
          you do, calls start. By the time you have numbers to compare, you&rsquo;re already fielding sales calls from
          multiple directions.
        </ComparisonCard>
        {/* Aurora column — new Figma copy (no doc equivalent); "vetted" dropped for doc-aligned, non-vouching wording */}
        {/* Body inverts the two cards to its left; bullets add what the body doesn't cover.
            No "licenses" claim — we surface reviews, NABCEP/BBB badges, and founded year, not license data. */}
        <DS.DarkModeProvider>
          <DS.Flex bg="black" borderRadius={12} gap={3} h="100%" p={6}>
            <H3>The Aurora marketplace</H3>
            <p>
              On Aurora, every estimate is modeled to your own roof and utility rate, so the numbers arrive on the same
              terms &mdash; not proposals built on different equipment and assumptions. Local installers compete for
              your project, and their offers sit side by side, so you can tell which is actually the better value
              without that work falling on you. Your name, email, and phone number stay yours until you decide to share
              them.
            </p>
            <DS.Flex gap={2} mt={2}>
              <CheckItem>No account needed to see prices</CheckItem>
              <CheckItem>Years in business, reviews, and accreditations</CheckItem>
              <CheckItem>Adjust panels or add a battery</CheckItem>
            </DS.Flex>
          </DS.Flex>
        </DS.DarkModeProvider>
      </ColumnSection>

      {/* "All the things we're not" (Figma: 2x2 X-circle cards). Doc headline, labels, and bodies. */}
      {/* "Drill" line (adapted from a Jon Franke blog post) and the "Not a ..." claims cleared to launch
          (Nick + Karen, 2026-08-03) — Aurora does not sell leads today, so that line is accurate. Legal is
          still discussing the wording; a suggested deletion of this card is pending in the copy doc. */}
      <Section align="center" pb={{ _: 7, lg: 7 }}>
        <H2>We don&rsquo;t show up at your house with a drill.</H2>
        <DS.Text maxW="760px" mx="auto" text="body16">
          Aurora runs the marketplace. Local installers do the work, and you stay in control of who you talk to.
        </DS.Text>
      </Section>
      <ColumnSection columnGap={7} columns={2} pt={{ _: 7, lg: 5 }} rowGap={7}>
        <NotCard label="Not an installer.">
          We connect you with local solar companies. They do the physical work.
        </NotCard>
        <NotCard label="Not a lead seller.">
          Your information stays yours. We don&rsquo;t sell your contact details to whoever&rsquo;s paying this month.
          Your email and phone number go only to the installer you choose, if you choose one.
        </NotCard>
        <NotCard label="Not a financing company.">
          Aurora isn&rsquo;t a lender or a loan broker, and we don&rsquo;t arrange or recommend financing. We display
          payment options offered by third parties so you can compare them.
        </NotCard>
        <NotCard label="Not a one-size-fits-all estimate.">
          Your estimate is modeled to your address, electricity bill, and utility rate &mdash; not a regional average
          applied to a generic system.
        </NotCard>
      </ColumnSection>

      {/* S4 — Three things we built this around (doc headline) */}
      <Section align="center" bg="uiBgAlternate" pb={{ _: 7, lg: 7 }}>
        <H2>Three things we built this around.</H2>
      </Section>
      <ColumnSection bg="uiBgAlternate" columnGap={9} columns={3} pb={{ _: 7, lg: 7 }} pt={{ _: 7, lg: 5 }} rowGap={7}>
        <DS.Flex flex={1} gap={4}>
          <DS.IconLockClosed size={{ _: 6, md: 8 }} />
          <H3>Your contact details are yours.</H3>
          <p>
            Enter your address to get started &mdash; that&rsquo;s what we use to model your estimate. Your email and
            phone number go only to the installer you choose, only when you&rsquo;re ready. Aurora doesn&rsquo;t sell
            your contact information to every company on the comparison page, or to anyone you haven&rsquo;t actively
            selected.
          </p>
        </DS.Flex>
        <DS.Flex flex={1} gap={4}>
          <DS.IconChartComparison size={{ _: 6, md: 8 }} />
          <H3>Estimates before contact every time.</H3>
          <p>
            You see the full picture before you&rsquo;re committed to talking to anyone. System size, costs, and
            available payment options &mdash; cash, loan, and lease or power purchase agreement &mdash; are priced side
            by side for your home. Adjust panels, add a battery, and the estimate updates live. Every estimate is
            modeled to your address, electricity bill, and utility rate, not a regional average applied to a generic
            system. No account needed. No callback required to get a number.
          </p>
        </DS.Flex>
        <DS.Flex flex={1} gap={4}>
          <DS.IconCheckCircle size={{ _: 6, md: 8 }} />
          <H3>Installers compete. You decide.</H3>
          {/* Closing line + installer-offers language cleared to launch (2026-08-03); mirrors the
              How It Works "vetted" decision — we describe what we show, we don't vouch. */}
          <p>
            Aurora doesn&rsquo;t rank installers, endorse any company, or steer you toward a particular offer.
            Installers on Aurora Marketplace who serve your area present their actual offers. You weigh the whole
            package &mdash; costs, reviews, accreditations, years in business. The decision stays yours; we just make it
            possible to decide clearly.
          </p>
        </DS.Flex>
      </ColumnSection>
      {/* Pull quote — Legal-cleared (per Nick, 2026-07-23). Figma pairs a trust CTA here; kept in S5 per doc to avoid duplication. */}
      <Section bg="uiBgAlternate" pt={{ _: 7, lg: 7 }}>
        <DS.Flex
          bg="white"
          border="1px solid"
          borderColor="uiDivider"
          borderRadius={12}
          gap={4}
          maxW="900px"
          mx="auto"
          p={{ _: 6, lg: 8 }}
        >
          <DS.Text as="blockquote" text={{ _: 'h3', md: 'h2' }}>
            &ldquo;I am a very price-sensitive person, so I was particularly concerned about that &mdash; but I was able
            to do a lot of research [with Aurora Marketplace] on different products to make my decision.&rdquo;
          </DS.Text>
          <DS.Text color="uiHelperCopy" text="body16">
            &mdash; Richard, Georgia
          </DS.Text>
        </DS.Flex>
      </Section>

      {/* S5 — The company behind the marketplace (doc: split layout, copy left, visual right) */}
      {/* Imagery rights fine: this is a consumer photo from lp/assets already live on easy-button and the
          state landing pages. The doc's open item was about B2B platform imagery, which we don't use here. */}
      <Section
        imageRight={{ alt: 'Homeowners reviewing their energy bills', height: 520, src: seniorCoupleReviewingBills }}
      >
        <H2>Over a decade in solar. Built for the homeowner.</H2>
        <p>
          Most homeowners have never heard of Aurora Solar, and that makes sense. For more than ten years we&rsquo;ve
          made the software that solar professionals use to design and price their systems. More than 7,000 solar
          companies run on Aurora, from local contractors to the biggest installers in the country, and pros have
          designed over 20 million solar projects on it. The installer who comes to your home may well use Aurora every
          day.
        </p>
        <p>
          We spent that decade watching how solar gets sold, and seeing where the process leaves homeowners guessing. We
          built the marketplace to change that: a place to see estimates for your home, compare them, and decide on your
          own time.
        </p>
        <p>
          Trust is the second-biggest reason homeowners hold back on solar, right after cost. We know because we track
          it. Every quarter we ask 1,000 U.S. homeowners about their real experiences with solar companies, and we
          publish what they tell us.
        </p>
        <ButtonLink
          event={{ name: TRACKING.LANDING_CLICK_TRUST_SIGNAL }}
          icon={DS.IconArrowRight}
          iconPosition="right"
          path="/home-solar/trust-signal"
          size="md"
          variant="tertiary"
        >
          See the latest results
        </ButtonLink>
      </Section>

      {/* Credibility strip (Figma: TIME recognition + AS SEEN IN press logos, exported from Figma) */}
      <Section align="center" bg="uiBgAlternate">
        {/* H2 helper gives Grace's 24px mobile / 44px desktop and matches the other section headings */}
        <H2 maxW="900px" mb={{ md: 6 }} mx="auto">
          <span>Named a </span>
          <strong>Top GreenTech Company by TIME magazine</strong>
          <span> for three years running.</span>
        </H2>
        <AsSeenIn />
      </Section>

      {/* S6 — CTA band (doc: #FEC351 yellow background) */}
      <Section align="center" bg="uiButtonCta.defaultBgColor">
        <H2>See what solar costs for your own home.</H2>
        <x.p color="black" maxW="600px" mx="auto">
          No account. No contact information required. No sales calls until you&rsquo;re ready.
        </x.p>
        <GetEstimatesButton />
      </Section>
    </article>
  );
};
